import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { MuiFileInput } from 'mui-file-input';
import { FC, useContext, useState } from 'react';
import { toast } from 'react-toastify';

import { useUploadDocument } from '@features/digitalOnboarding/hooks/useUploadDocument';
import { Close } from '@mui/icons-material';
import { UploadLogoIcon } from '../../../../vectors/index';
import { authContext } from '@staffedupusa/shared-components';
import { selectedPlanName } from '@utils/pricing';
import { track } from '@amplitude/analytics-browser';
import { amplitudeEventsName } from '@utils/amplitudeEvents';

type UploadCustomDocumentModalProps = {
  isOpen: boolean;
  selectedCompanyId?: number | null;
  handleClose: () => void;
};

const UploadCustomDocumentModal: FC<UploadCustomDocumentModalProps> = ({
  isOpen,
  selectedCompanyId,
  handleClose,
}) => {
  const { authData } = useContext(authContext);
  const isCompamy = authData?.user?.company;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);
  const [file, setFile] = useState<File | null>(null);
  const [errorText, setErrorText] = useState<string>('');
  const { uploadDocument, isUploadingDocument } = useUploadDocument();

  const handleUploadFile = () => {
    if (!file) {
      setErrorText('File is required');
      return;
    }

    isCompamy &&
      track(
        `${amplitudeEventsName.ONBOARDING_DOCUMENT_UPLOADED}-${selectedPlan}`,
      );
    uploadDocument(
      {
        document: file,
        ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
      },
      {
        onSuccess: () => {
          setErrorText('');
          toast.success(
            'Your request has been sent! We will get your document entered into the system.',
          );
          setFile(null);
          handleClose();
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <UploadLogoIcon />
        <br />
        <Typography variant="h4">Upload Onboarding Documents</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ marginBottom: 3, mt: 2 }}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <MuiFileInput
            variant="outlined"
            size="small"
            label="Upload file"
            value={file!}
            onChange={(newFile) => {
              setFile(newFile);
            }}
            onClick={() =>
              isCompamy &&
              track(
                `${amplitudeEventsName.ONBOARDING_CLICK_UPLOAD_DOCUMENT_FIELD}-${selectedPlan}`,
              )
            }
            InputProps={{
              placeholder: 'true',
              inputProps: {
                accept: '.pdf, .doc, .docx',
                placeholder: 'false',
              },
            }}
            sx={{ marginTop: 2 }}
          />
          <LoadingButton
            variant="contained"
            loadingPosition="start"
            loading={isUploadingDocument}
            startIcon={<FileUploadOutlinedIcon />}
            onClick={handleUploadFile}
          >
            Upload Document
          </LoadingButton>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default UploadCustomDocumentModal;
